export type WalaSignature = {
  name: string;
  role: string;
  signedAt: number;
  /**
   * The incident position the signer held, resolved from the roster by the
   * server. Absent on signatures recorded before it was captured.
   */
  positionTitle?: string;
  /**
   * Who signed, stamped by the server. Only boxes that take more than one
   * signature need it, to tell one signer's row from another's.
   */
  userGuid?: string;
  /**
   * Free-text comments captured at sign time for boxes that print a Comments
   * field (ICS-213RR Section Chief / Command Staff Approval).
   */
  comments?: string;
};

export type SignatureCompletionStatus = 'draft' | 'partially-signed' | 'signed';

export const ICS204_PREPARED_BY_SIGN_ROLE = 'Prepared By';
export const ICS204_PSC_REVIEW_ROLE = 'Planning Section Chief';
export const ICS204_OSC_REVIEW_ROLE = 'Operations Section Chief';

export type SignatureSlot = {
  id: string;
  label: string;
  boxLabel: string;
  role: string;
  isComplete: (signatures: WalaSignature[]) => boolean;
  findSignature: (signatures: WalaSignature[]) => WalaSignature | undefined;
};

export const ICS204_REQUIRED_SIGNATURE_SLOTS: SignatureSlot[] = [
  {
    id: 'prepared-by',
    label: 'Prepared By',
    boxLabel: '9. Prepared By',
    role: ICS204_PREPARED_BY_SIGN_ROLE,
    isComplete: (signatures) => signatures.length > 0 && signatures[0] != null,
    findSignature: (signatures) => signatures[0],
  },
  {
    id: 'review-psc',
    label: ICS204_PSC_REVIEW_ROLE,
    boxLabel: '10. Reviewed by (PSC)',
    role: ICS204_PSC_REVIEW_ROLE,
    isComplete: (signatures) =>
      signatures.some(
        (signature, index) =>
          index > 0 && signature.role === ICS204_PSC_REVIEW_ROLE
      ),
    findSignature: (signatures) =>
      signatures.find(
        (signature, index) =>
          index > 0 && signature.role === ICS204_PSC_REVIEW_ROLE
      ),
  },
  {
    id: 'review-osc',
    label: ICS204_OSC_REVIEW_ROLE,
    boxLabel: '11. Reviewed by (OSC/ISC)',
    role: ICS204_OSC_REVIEW_ROLE,
    isComplete: (signatures) =>
      signatures.some(
        (signature, index) =>
          index > 0 && signature.role === ICS204_OSC_REVIEW_ROLE
      ),
    findSignature: (signatures) =>
      signatures.find(
        (signature, index) =>
          index > 0 && signature.role === ICS204_OSC_REVIEW_ROLE
      ),
  },
];

export function parseWalaSignatures(value: unknown): WalaSignature[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is WalaSignature =>
      !!entry &&
      typeof entry === 'object' &&
      typeof (entry as WalaSignature).name === 'string' &&
      typeof (entry as WalaSignature).role === 'string' &&
      typeof (entry as WalaSignature).signedAt === 'number'
  );
}

/**
 * What the version chrome actually reads off a completion. Kept narrower than
 * `getSignatureCompletion`'s return so forms with descriptor-derived slots can
 * supply their own progress to the same status bar, dialogs, and ring.
 */
export type SignatureCompletion = {
  status: SignatureCompletionStatus;
  completedCount: number;
  requiredTotal: number;
  percent: number;
  slots: { id: string; label: string; complete: boolean }[];
};

export function getSignatureCompletion(
  signatures: WalaSignature[] | null | undefined
) {
  const safeSignatures = signatures ?? [];
  const requiredTotal = ICS204_REQUIRED_SIGNATURE_SLOTS.length;
  const slots = ICS204_REQUIRED_SIGNATURE_SLOTS.map((slot) => ({
    ...slot,
    complete: slot.isComplete(safeSignatures),
  }));
  const completedCount = slots.filter((slot) => slot.complete).length;

  let status: SignatureCompletionStatus = 'draft';
  if (safeSignatures.length === 0 || completedCount === 0) {
    status = 'draft';
  } else if (completedCount >= requiredTotal) {
    status = 'signed';
  } else {
    status = 'partially-signed';
  }

  return {
    status,
    completedCount: Math.min(completedCount, requiredTotal),
    requiredTotal,
    percent:
      requiredTotal === 0
        ? 0
        : Math.round(
            (Math.min(completedCount, requiredTotal) / requiredTotal) * 100
          ),
    slots,
  };
}

export function isVersionLockedBySignatures(
  signatures: WalaSignature[] | null | undefined
): boolean {
  return getSignatureCompletion(signatures).status !== 'draft';
}

export function isVersionFullySigned(
  signatures: WalaSignature[] | null | undefined
): boolean {
  return getSignatureCompletion(signatures).status === 'signed';
}

export function formatSignatureCompletionStatus(
  status: SignatureCompletionStatus
): string {
  switch (status) {
    case 'partially-signed':
      return 'partially signed';
    case 'signed':
      return 'signed';
    default:
      return 'unsigned draft';
  }
}

/** Banner / list wording aligned with Status column draft labels. */
export function formatSignatureDraftLabel(
  status: SignatureCompletionStatus
): string {
  switch (status) {
    case 'partially-signed':
      return 'partially signed draft';
    case 'signed':
      return 'signed draft';
    default:
      return 'unsigned draft';
  }
}

export function formatSignatureDateTime(signedAt: number): string {
  const date = new Date(signedAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

/**
 * Status column: operational label (Draft replaced by signature draft state)
 * plus always-visible N/3 Signatures line.
 */
export function formatIcs204StatusCell({
  proposedStatusLabel,
  signatures,
}: {
  proposedStatusLabel: string | null | undefined;
  signatures: WalaSignature[] | null | undefined;
}): { primary: string; signaturesLine: string } {
  const completion = getSignatureCompletion(signatures);
  const signaturesLine = `${completion.completedCount}/${completion.requiredTotal} Signatures`;
  const isDraftLabel =
    !proposedStatusLabel ||
    proposedStatusLabel === 'Draft' ||
    proposedStatusLabel === '—';

  if (isDraftLabel) {
    switch (completion.status) {
      case 'partially-signed':
        return { primary: 'Partially signed draft', signaturesLine };
      case 'signed':
        return { primary: 'Signed draft', signaturesLine };
      default:
        return { primary: 'Unsigned draft', signaturesLine };
    }
  }

  return {
    primary: proposedStatusLabel,
    signaturesLine,
  };
}
